import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    weddingDate: '',
    guestCount: '',
    packageInterest: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePackageChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      packageInterest: value
    }));
  };

  const getPackageName = (value) => {
    const packages = {
      'silver': 'Silver Package (₹2,99,999)',
      'gold': 'Gold Package (₹6,99,999)',
      'platinum': 'Platinum Package (₹9,99,999)',
      'custom': 'Custom Package'
    };
    return packages[value] || value;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Create WhatsApp message with inquiry details
    const whatsappMessage = `🎊 *New Wedding Inquiry*%0A%0A` +
      `*Name:* ${formData.name}%0A` +
      `*Email:* ${formData.email}%0A` +
      `*Phone:* ${formData.phone}%0A` +
      `${formData.weddingDate ? `*Wedding Date:* ${formData.weddingDate}%0A` : ''}` +
      `${formData.guestCount ? `*Guests:* ${formData.guestCount}%0A` : ''}` +
      `${formData.packageInterest ? `*Package:* ${getPackageName(formData.packageInterest)}%0A` : ''}` +
      `${formData.message ? `*Message:* ${formData.message}` : ''}`;

    // Open WhatsApp with the message
    window.open(`https://wa.me/919873514657?text=${whatsappMessage}`, '_blank');

    toast.success('Redirecting to WhatsApp!', {
      description: 'Please send the message to complete your inquiry. We\'ll respond within 24 hours!'
    });

    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      weddingDate: '',
      guestCount: '',
      packageInterest: '',
      message: ''
    });
    
    setIsSubmitting(false);
  };

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Let's Plan Your Dream Wedding
          </h2>
          <p className="text-lg text-gray-600">
            Ready to experience the magic of a Rishikesh wedding at the best prices? 
            Share your vision, and we'll create a personalized quote within 24 hours.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {/* Contact Form */}
          <Card className="lg:col-span-2 p-8 border border-gray-200">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Name */}
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    required
                    className="mt-2"
                  />
                </div>

                {/* Email */}
                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    required
                    className="mt-2"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Phone */}
                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+91 98765 43210"
                    required
                    className="mt-2"
                  />
                </div>

                {/* Wedding Date */}
                <div>
                  <Label htmlFor="weddingDate">Tentative Wedding Date</Label>
                  <Input
                    id="weddingDate"
                    name="weddingDate"
                    type="date"
                    value={formData.weddingDate}
                    onChange={handleChange}
                    className="mt-2"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Guest Count */}
                <div>
                  <Label htmlFor="guestCount">Expected Guest Count</Label>
                  <Input
                    id="guestCount"
                    name="guestCount"
                    type="number"
                    value={formData.guestCount}
                    onChange={handleChange}
                    placeholder="e.g., 80"
                    className="mt-2"
                  />
                </div>

                {/* Package Interest */}
                <div>
                  <Label htmlFor="packageInterest">Package Interest</Label>
                  <Select onValueChange={handlePackageChange} value={formData.packageInterest}>
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Select a package" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="silver">Silver Package (40-50 Guests)</SelectItem>
                      <SelectItem value="gold">Gold Package (80-100 Guests)</SelectItem>
                      <SelectItem value="platinum">Platinum Package (125-150 Guests)</SelectItem>
                      <SelectItem value="custom">Custom Package</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Message */}
              <div>
                <Label htmlFor="message">Tell Us About Your Vision</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Share any specific requirements, themes, or questions..."
                  rows={5}
                  className="mt-2"
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                size="lg"
                className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Get Your Free Quote'}
              </Button>

              <p className="text-sm text-gray-500 text-center">
                We respect your privacy. Your information will never be shared with third parties.
              </p>
            </form>
          </Card>

          {/* Contact Info Sidebar */}
          <div className="space-y-6">
            {/* Contact Card */}
            <Card className="p-6 border border-gray-200 bg-gradient-to-br from-amber-50 to-emerald-50">
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                Get In Touch
              </h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Phone size={20} className="text-amber-700 flex-shrink-0 mt-1" />
                  <div className="flex flex-col space-y-1">
                    <div className="font-medium text-gray-900">Phone</div>
                    <a href="tel:+919873514657" className="text-gray-600 hover:text-amber-700 text-sm">
                      +91 98735 14657
                    </a>
                    <a href="tel:+919811173781" className="text-gray-600 hover:text-amber-700 text-sm">
                      +91 98111 73781
                    </a>
                    <a href="tel:+919837272408" className="text-gray-600 hover:text-amber-700 text-sm">
                      +91 98372 72408
                    </a>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Mail size={20} className="text-amber-700 flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-medium text-gray-900">Email</div>
                    <a href="mailto:jeevanutsavyoga@gmail.com" className="text-gray-600 hover:text-amber-700 text-sm">
                      jeevanutsavyoga@gmail.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <MapPin size={20} className="text-amber-700 flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-medium text-gray-900">Location</div>
                    <div className="text-gray-600 text-sm">
                      Jeevan Utsav Retreat<br />
                      Ganga Boghpur Talla<br />
                      Chilla Dam Road<br />
                      Uttarakhand
                    </div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Clock size={20} className="text-amber-700 flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-medium text-gray-900">Hours</div>
                    <div className="text-gray-600 text-sm">
                      Mon-Sat: 9:00 AM - 7:00 PM<br />
                      Sun: 10:00 AM - 5:00 PM
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Quick Response Card */}
            <Card className="p-6 border border-amber-200 bg-amber-50">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                24-Hour Response Guarantee
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                We understand wedding planning is time-sensitive. Expect a detailed response 
                with your personalized quote within 24 hours.
              </p>
              <div className="flex items-center space-x-2 text-sm text-amber-800 font-medium">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span>Available now</span>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
